import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";
import icon from "../../assets/icons/icon.png";
import line from "../../assets/images/line.avif";
import profilePic from "../../assets/images/profile_image.png";
import withCacheBust from "../../utils/withCacheBust";
import { ROUTES } from "../../utils/appRoutes";
import { buildAuthPathWithNext, getCurrentAppPath, getSafeNextFromLocation } from "../../utils/authRedirect";
import { formatNavbarDisplayName } from "../../utils/profileDisplayName";

function Navbar({ isAuthenticated, handleLogout, onUserFetch, refreshTokens, userData, profilePictureVersion }) {
  const [isExpanded, setIsExpanded] = useState(false);
  // Set when the profile fetch fails for a reason a retry won't fix, so the
  // name doesn't sit on "Loading..." forever.
  const [profileUnavailable, setProfileUnavailable] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const isAuthPage = [
    ROUTES.LOGIN,
    ROUTES.REGISTER,
    ROUTES.CONFIRM,
    ROUTES.FORGOT_PASSWORD,
    ROUTES.RESET_PASSWORD,
  ].includes(location.pathname);
  const currentPath = getCurrentAppPath(location);
  const intendedNext = getSafeNextFromLocation(
    location,
    isAuthPage ? ROUTES.HOME : currentPath
  );
  const loginPath = buildAuthPathWithNext(ROUTES.LOGIN, intendedNext);
  const registerPath = buildAuthPathWithNext(ROUTES.REGISTER, intendedNext);
  const displayName = userData
    ? formatNavbarDisplayName(userData)
    : profileUnavailable
      ? "Account"
      : "Loading...";

  const toggleDropdown = (event) => {
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const handleItemClick = (path) => {
    setIsExpanded(false);
    navigate(path);
  };

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      !buttonRef.current.contains(event.target)
    ) {
      setIsExpanded(false);
    }
  };

  const handleLogoutClick = () => {
    setIsExpanded(false);
    handleLogout(); // Clear tokens and authentication
    navigate("/"); // Redirect to home after logout
  };

  useEffect(() => {
    // Only run if user is authenticated
    if (!isAuthenticated) return;
  
    let isMounted = true; // To prevent state updates if component unmounts
  
    const fetchUserData = async () => {
      let idToken = localStorage.getItem("idToken");
      if (!idToken) return;
  
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/profile/user-profile`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          }
        );
  
        if (response.status === 401) {
          // Attempt token refresh
          const tokenRefreshed = await refreshTokens(); // from props
          if (tokenRefreshed) {
            idToken = localStorage.getItem("idToken");
            // Retry the user-profile fetch after refresh
            const retryResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/profile/user-profile`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${idToken}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (!retryResponse.ok) throw new Error("Failed to fetch after refresh");
            const data = await retryResponse.json();
            if (isMounted && onUserFetch) {
              onUserFetch(data);
            }
          } else {
            // If refresh fails, log the user out
            if (isMounted) handleLogoutClick();
          }
        } else if (response.status === 404) {
          // The token is valid but this backend has no such user, so the
          // session can never load a profile. Sign out rather than leaving the
          // navbar authenticated and stuck on "Loading...".
          console.error("Profile not found for the signed-in account; signing out.");
          if (isMounted) {
            setProfileUnavailable(true);
            handleLogoutClick();
          }
        } else {
          // If status isn't 401
          if (!response.ok) throw new Error("Failed to fetch user data");
          const data = await response.json();
          if (isMounted && onUserFetch) {
            onUserFetch(data);
          }
        }
      } catch (err) {
        console.error(err);
        // Transient failure (network/5xx): keep the session, but stop the
        // name from rendering "Loading..." indefinitely.
        if (isMounted) setProfileUnavailable(true);
      }
    };
  
    fetchUserData();
  
    return () => {
      // Clean up if component unmounts while fetch is in progress
      isMounted = false;
    };
  // Only depend on isAuthenticated
  }, [isAuthenticated]);
  

  return (
    <>
      <nav className={`navbar ${isExpanded ? "expanded" : ""}`}>
        <div className="navbar-container">
          {/* Left section */}
          <div className="nav-left">
            <div className="nav-item" onClick={() => navigate("/")}>
              ZEAL
            </div>
          </div>

          {/* Center icon */}
          <div className="nav-center">
            <img
              src={icon}
              alt="Icon"
              className="nav-icon"
              onClick={toggleDropdown}
              ref={buttonRef}
            />
          </div>

          {/* Right side: user info or start link */}
          <div className="nav-right">
            <div className="nav-items">
              {isAuthenticated ? (
                /* -------------- LOGGED IN -------------- */
                <div className="nav-item nav-item--user" onClick={() => navigate("/profile")}>
                  <span className="nav-user-name">{displayName}</span>
                  <img
                    src={
                      userData && userData.profilePictureUrl
                        ? withCacheBust(userData.profilePictureUrl, profilePictureVersion)
                        : profilePic
                    }
                    alt={
                      userData
                        ? `${displayName}'s avatar`
                        : "User avatar"
                    }
                    className="avatar"
                  />
                </div>
              ) : (
                /* -------------- NOT LOGGED IN -------------- */
                <>
                  {location.pathname === "/" && (
                    <div
                      className="nav-item"
                      onClick={() => navigate(loginPath)}
                    >
                      Start
                      <img
                        src={profilePic}
                        alt="Default avatar"
                        className="avatar"
                      />
                    </div>
                  )}

                  {location.pathname === "/register" && (
                    <div className="nav-item">
                      <Link to={loginPath}>Login</Link>
                    </div>
                  )}

                  {location.pathname === "/login" && (
                    <div className="nav-item">
                      <Link to={registerPath}>Register</Link>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* <div className="nav-menu-icon" onClick={toggleDropdown}>
              &#9776;
            </div> */}
          </div>
        </div>
      </nav>

      {/* Expanded dropdown */}
      {isExpanded && (
        <div
          className="dropdown-overlay"
          ref={dropdownRef}
          onClick={() => setIsExpanded(false)}
        >
          <div className="expanded-menu" onClick={(e) => e.stopPropagation()}>
            <div className="line">
              <img src={line} alt="Line" className="line-img" />
            </div>
            <div className="dropdown-item" onClick={() => handleItemClick("/")}>
              Home
            </div>
            <div className="dropdown-item" onClick={() => handleItemClick("/")}>
              CHOOSE A GAME
            </div>

            {isAuthenticated ? (
              <>
                <div
                  className="dropdown-item"
                  onClick={() => handleItemClick("/profile")}
                >
                  PROFILE
                </div>
                <div className="dropdown-item" onClick={handleLogoutClick}>
                  LOG OUT
                </div>
              </>
            ) : (
              <>
                <div
                  className="dropdown-item"
                  onClick={() => handleItemClick(loginPath)}
                >
                  LOGIN
                </div>
                <div
                  className="dropdown-item"
                  onClick={() => handleItemClick(registerPath)}
                >
                  REGISTER
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
